import React, { useState, useEffect } from 'react';
import { withStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QRCode from 'react-qr-code';
import DocumentMSStyle from './DocumentMSStyle';
import { getDashboardSummary, getDocuments, getAssignments, updateAssignmentStatus, getUsers, deleteDocument, getDocumentDetail } from './dmsApi';
import DocumentUpload from './DocumentUpload';
import DocumentDetail from './DocumentDetail';
import AssignDocumentDialog from './AssignDocumentDialog';
import SendPackageDialog from './SendPackageDialog';
import EditDocumentDialog from './EditDocumentDialog';
import DocumentHistory from './DocumentHistory';
import Notifications from './Notifications';

const DashboardSummary = (props) => {
  const { classes } = props;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_documents: 0,
    total_assignments: 0,
    pending: 0,
    completed: 0,
  });
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(null); // Will store document object or null
  const [aiSummaryOpen, setAiSummaryOpen] = useState(null);
  const [sendPackageDialogOpen, setSendPackageDialogOpen] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [documentForHistory, setDocumentForHistory] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [assignmentForQr, setAssignmentForQr] = useState(null);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [assignmentForStatusUpdate, setAssignmentForStatusUpdate] = useState(null);
  const [statusUpdateStep, setStatusUpdateStep] = useState(0);
  const [statusUpdateData, setStatusUpdateData] = useState({
    pending: { notes: '' },
    processing: { notes: '' },
    archived: { notes: '' },
  });

  useEffect(() => {
    fetchData();
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await getUsers('pwc');
      if (response?.results && Array.isArray(response.results)) {
        setEmployees(response.results);
      } else if (Array.isArray(response)) {
        setEmployees(response);
      } else {
        setEmployees([]);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      setEmployees([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      const privilege = localStorage.getItem('previlage') || sessionStorage.getItem('previlage');
      
      // Fetch summary, documents, and assignments in parallel
      const [summaryData, documentsData, assignmentsData] = await Promise.all([
        getDashboardSummary(userId ? { user_id: userId } : {}),
        getDocuments(),
        getAssignments(),
      ]);
      
      setSummary(summaryData);
      const allDocs = Array.isArray(documentsData) ? documentsData : [];
      const allAssignments = Array.isArray(assignmentsData) ? assignmentsData : [];
      setAssignments(allAssignments);
      
      // Filter documents based on privilege
      if (privilege === 'dms_user' && userId) {
        // Get document IDs assigned to this user
        const assignedDocumentIds = allAssignments
          .filter((a) => 
            a.assigned_to === userId || 
            a.assigned_to === String(userId) ||
            (typeof a.assigned_to === 'object' && (a.assigned_to?._id === userId || a.assigned_to?.id === userId))
          )
          .map((a) => a.record_id);
        
        // Filter documents to only show assigned ones
        const filteredDocs = allDocs.filter((doc) => 
          assignedDocumentIds.includes(doc.id) || assignedDocumentIds.includes(String(doc.id))
        );
        setDocuments(filteredDocs);
      } else {
        // For non-user privileges, show all documents
        setDocuments(allDocs);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (document) => {
    try {
      const { getDocumentDetail } = await import('./dmsApi');
      const detail = await getDocumentDetail(document.id);
      setSelectedDocument(detail);
      setDetailOpen(true);
    } catch (err) {
      setError('Failed to load document details');
      console.error('Detail error:', err);
    }
  };

  const handleAssign = (document) => {
    setAssignDialogOpen(document);
  };

  const handleDelete = (document) => {
    setDeleteConfirmOpen(document);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmOpen) return;
    
    try {
      setDeleting(true);
      await deleteDocument(deleteConfirmOpen.id);
      setDeleteConfirmOpen(null);
      fetchData(); // Refresh the data
      setError(null);
    } catch (err) {
      setError(err.detail || err.error || 'Failed to delete document');
      console.error('Delete document error:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleAssignmentSuccess = () => {
    fetchData();
    if (selectedDocument) {
      handleViewDetail(selectedDocument);
    }
  };

  const handleEditDocument = (document) => {
    console.log('Opening edit dialog for document:', document);
    setDocumentToEdit(document);
    setEditDialogOpen(true);
  };

  const handleViewHistory = async (document) => {
    try {
      const documentId = document?.id || document?.record_id;
      if (documentId) {
        const detail = await getDocumentDetail(documentId);
        setDocumentForHistory(detail);
      } else {
        setDocumentForHistory(document);
      }
      setHistoryOpen(true);
    } catch (err) {
      // If detail fetch fails, use the document directly
      setDocumentForHistory(document);
      setHistoryOpen(true);
    }
  };

  const handleEditSuccess = () => {
    fetchData();
    setEditDialogOpen(false);
    setDocumentToEdit(null);
  };

  const getAssignmentCounts = (documentId) => {
    const docAssignments = assignments.filter((a) => a.record_id === documentId);
    const total = docAssignments.length;
    const done = docAssignments.filter((a) => a.current_status === 'completed').length;
    const pending = docAssignments.filter((a) => 
      a.current_status === 'assigned' || 
      a.current_status === 'sent_via_jiffy' || 
      a.current_status === 'collected_via_jiffy' || 
      a.current_status === 'sent_via_other' || 
      a.current_status === 'processing'
    ).length;
    return { total, done, pending };
  };

  // Use summary data from API
  // The summary object contains: total_documents, total_assignments, pending, completed
  // For "In Progress", we'll calculate it from assignments if not provided by API
  const getInProgressCount = () => {
    if (summary.in_progress !== undefined) {
      return summary.in_progress;
    }
    // Fallback: calculate from assignments if API doesn't provide it
    return assignments.filter((a) => 
      a.current_status === 'sent_via_jiffy' || 
      a.current_status === 'collected_via_jiffy' || 
      a.current_status === 'sent_via_other' || 
      a.current_status === 'processing'
    ).length;
  };

  const getStatusColor = (status) => {
    const statusMap = {
      assigned: '#FF9800', // Orange
      sent_via_jiffy: '#0F1638', // Blue
      collected_via_jiffy: '#9C27B0', // Purple
      sent_via_other: '#00BCD4', // Cyan
      processing: '#0F1638', // Blue
      completed: '#4CAF50', // Green
    };
    return statusMap[status] || '#6E759A';
  };

  const getStatusLabel = (status) => {
    if (!status) return '-';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getDocumentName = (recordId) => {
    const doc = documents.find((d) => d.id === recordId);
    return doc ? doc.document_name || `Document #${recordId}` : `Document #${recordId}`;
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return '-';
    const employee = employees.find((e) => e._id === employeeId || e.id === employeeId || e.user_id === employeeId);
    return employee ? (employee.name || employee.user_name || employee.email || employeeId) : employeeId;
  };

  const generateTrackingUrl = (assignment) => {
    if (!assignment || !assignment.record_id) return '';
    
    // Use current timestamp (latest) - Unix timestamp in seconds
    const timestamp = Math.floor(Date.now() / 1000);
    
    return `https://stagedmstracking.jiffy.ae/tracking/${assignment.record_id}/${timestamp}`;
  };

  const handleShowQrCode = (assignment) => {
    setAssignmentForQr(assignment);
    setQrDialogOpen(true);
  };

  const handleShowDocumentQrCode = (document) => {
    // Create a mock assignment-like object for the document QR code dialog
    const mockAssignment = {
      record_id: document.id,
      id: document.id,
    };
    setAssignmentForQr(mockAssignment);
    setQrDialogOpen(true);
  };

  const handleStatusChange = async (assignmentId, newStatus) => {
    try {
      // If selecting "sent_via_jiffy", navigate to sendpackage route
      if (newStatus === 'sent_via_jiffy') {
        navigate('/sendpackage');
        return;
      }
      
      // If selecting "collected_via_jiffy", navigate to collectpackage route
      if (newStatus === 'collected_via_jiffy') {
        navigate('/collectpackage');
        return;
      }
      
      // For other statuses, update normally
      setUpdatingStatus({ ...updatingStatus, [assignmentId]: true });
      // Use the correct API endpoint for status update
      await updateAssignmentStatus(assignmentId, newStatus, '', null);
      // Refresh assignments and summary after update
      const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
      const [assignmentsData, summaryData] = await Promise.all([
        getAssignments(),
        getDashboardSummary(userId ? { user_id: userId } : {}),
      ]);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to update assignment status:', err);
      setError(err.detail || err.error || 'Failed to update status');
    } finally {
      setUpdatingStatus({ ...updatingStatus, [assignmentId]: false });
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.error} style={{ textAlign: 'center', padding: '20px' }}>
        {error}
      </Box>
    );
  }

  return (
    <Box style={{ padding: '0' }}>
      {/* Summary Cards */}
      <Grid container spacing={3} style={{ marginBottom: '40px' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: '#F0F4FF',
              }}
            >
              <img 
                src="./Images/tab.svg" 
                alt="Total Assigned" 
                style={{ width: '32px', height: '32px' }}
              />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {summary.total_assignments || 0}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Total Assigned
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: '#FFF4E6',
              }}
            >
              <img 
                src="./Images/tab2.svg" 
                alt="Pending" 
                style={{ width: '32px', height: '32px' }}
              />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {summary.pending || 0}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Pending
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: '#E3F2FD',
              }}
            >
              <img 
                src="./Images/tab3.svg" 
                alt="In Progress" 
                style={{ width: '32px', height: '32px' }}
              />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {getInProgressCount()}
              </Typography>
              <Typography className={classes.summaryLabel}>
                In Progress
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            className={classes.summaryCard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Box
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: '#E8F5E9',
              }}
            >
              <img 
                src="./Images/tab4.svg" 
                alt="Completed" 
                style={{ width: '32px', height: '32px' }}
              />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Typography className={classes.summaryValue}>
                {summary.completed || 0}
              </Typography>
              <Typography className={classes.summaryLabel}>
                Completed
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Notifications Section */}
      <Box style={{ marginBottom: '40px' }}>
        <Notifications />
      </Box>

      {/* Documents Section - Hide for user privilege */}
      {(() => {
        const privilege = localStorage.getItem('previlage') || sessionStorage.getItem('previlage');
        if (privilege === 'dms_user') return null;
        
        return (
          <Box style={{ marginBottom: '40px' }}>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <Box>
            <Typography
              variant="h5"
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#131C4C',
                marginBottom: '4px',
                fontFamily: 'Montserrat',
                lineHeight: '1.2',
              }}
            >
              Documents
            </Typography>
            <Typography
              variant="body2"
              style={{
                fontSize: '14px',
                color: '#6E759A',
                fontFamily: 'Montserrat',
                lineHeight: '1.4',
              }}
            >
              Manage document templates and assignments
            </Typography>
          </Box>
          <DocumentUpload onSuccess={fetchData} />
        </Box>

        <TableContainer 
          component={Paper}
          style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Table className={classes.table} sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Case #</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Client</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Name</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell className={classes.tableHeader}>Description</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>AI Summary</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>File</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap', width: '280px', minWidth: '280px', maxWidth: '280px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '48px' }}>
                    <Typography style={{ color: '#6E759A', fontSize: '14px', fontFamily: 'Montserrat' }}>
                      No documents found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => {
                  const hasFile = doc.files && doc.files.length > 0;
                  const aiSummary = doc.files && doc.files[0] ? doc.files[0].summary : '';

                  return (
                    <TableRow key={doc.id} className={classes.tableRow}>
                      <TableCell 
                        className={classes.tableCell} 
                        style={{ 
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleEditDocument(doc)}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                            color: '#4F61C1',
                          },
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                            fontWeight: 500,
                          }}
                          sx={{
                            '&:hover': {
                              color: '#4F61C1',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {doc.case_number || `CASE-2025-${String(doc.id).padStart(3, '0')}`}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          {doc.client_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap', maxWidth: '200px' }}>
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {doc.document_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        {doc.document_type ? (
                          <Chip
                            label={doc.document_type}
                            size="small"
                            style={{
                              backgroundColor: '#E5E5E5',
                              color: '#131C4C',
                              fontSize: '12px',
                              fontFamily: 'Montserrat',
                              fontWeight: 500,
                              height: '24px',
                              borderRadius: '12px',
                            }}
                          />
                        ) : (
                          <Typography style={{ fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ maxWidth: '250px' }}>
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={doc.description || ''}
                        >
                          {doc.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        {aiSummary ? (
                          <Button
                            size="small"
                            startIcon={<AutoAwesomeIcon style={{ fontSize: '16px' }} />}
                            onClick={() => setAiSummaryOpen({ doc, summary: aiSummary })}
                            style={{
                              textTransform: 'none',
                              color: '#0F1638',
                              fontSize: '12px',
                              fontFamily: 'Montserrat',
                              padding: '4px 8px',
                              minWidth: 'auto',
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(15, 22, 56, 0.08)',
                              },
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography style={{ fontSize: '12px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        {hasFile ? (
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon style={{ fontSize: '16px' }} />}
                            onClick={() => {
                              if (doc.files[0].file_url) {
                                window.open(doc.files[0].file_url, '_blank');
                              }
                            }}
                            style={{
                              textTransform: 'none',
                              color: '#0F1638',
                              fontSize: '12px',
                              fontFamily: 'Montserrat',
                              padding: '4px 8px',
                              minWidth: 'auto',
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(15, 22, 56, 0.08)',
                              },
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography style={{ fontSize: '12px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                            No file
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap', width: '280px', minWidth: '280px', maxWidth: '280px' }}>
                        <Box style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'nowrap' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleAssign(doc)}
                            startIcon={<PersonIcon style={{ fontSize: '16px' }} />}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              borderColor: '#E0E0E0',
                              color: '#131C4C',
                              fontFamily: 'Montserrat',
                              fontSize: '12px',
                              padding: '4px 8px',
                              minWidth: 'auto',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                borderColor: '#BDBDBD',
                                backgroundColor: '#F5F5F5',
                              },
                              '& .MuiButton-startIcon': {
                                marginRight: '4px',
                                marginLeft: 0,
                              },
                            }}
                          >
                            Assign
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleShowDocumentQrCode(doc)}
                            title="Show QR Code"
                            sx={{ 
                              color: '#131C4C',
                              padding: '6px',
                              minWidth: '32px',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: 'rgba(19, 28, 76, 0.08)',
                              },
                            }}
                          >
                            <QrCodeIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditDocument(doc)}
                            title="Edit"
                            sx={{ 
                              color: '#131C4C',
                              padding: '6px',
                              minWidth: '32px',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(doc)}
                            title="Delete"
                            sx={{ 
                              color: '#131C4C',
                              padding: '6px',
                              minWidth: '32px',
                              width: '32px',
                              height: '32px',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
        );
      })()}

      {/* My Assigned Documents Section - Only show for user privilege */}
      {(() => {
        const privilege = localStorage.getItem('previlage') || sessionStorage.getItem('previlage');
        if (privilege !== 'dms_user') return null;
        
        return (
          <Box style={{ marginBottom: '40px' }}>
        <Box style={{ marginBottom: '24px' }}>
          <Typography
            variant="h5"
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#131C4C',
              marginBottom: '4px',
              fontFamily: 'Montserrat',
              lineHeight: '1.2',
            }}
          >
            My Assigned Documents
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: '14px',
              color: '#6E759A',
              fontFamily: 'Montserrat',
              lineHeight: '1.4',
            }}
          >
            Documents assigned to you for attestation
          </Typography>
        </Box>

        <TableContainer 
          component={Paper}
          style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Table className={classes.table} sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Document Name</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Case Number</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Assigned Date</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell className={classes.tableHeader}>Notes</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Scan QR</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap', width: '280px', minWidth: '280px', maxWidth: '280px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
                const myAssignments = assignments.filter((a) => 
                  a.assigned_to === userId || 
                  a.assigned_to === String(userId) ||
                  (typeof a.assigned_to === 'object' && (a.assigned_to?._id === userId || a.assigned_to?.id === userId))
                );
                
                if (myAssignments.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={7} style={{ textAlign: 'center', padding: '48px' }}>
                        <Typography style={{ color: '#6E759A', fontSize: '14px', fontFamily: 'Montserrat' }}>
                          No documents assigned to you
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                
                return myAssignments.map((assignment) => {
                  const doc = documents.find((d) => d.id === assignment.record_id);
                  
                  const getStatusBadgeStyle = (status) => {
                    // Color scheme matching the design: In Progress (Orange), Pending (Dark Blue), Completed (Green), Submitted (Blue), Attested (Darker Green)
                    const statusMap = {
                      assigned: { bg: '#6E759A', color: '#FFFFFF' }, // Dark Grey/Blue for Pending
                      pending: { bg: '#6E759A', color: '#FFFFFF' }, // Dark Grey/Blue
                      in_progress: { bg: '#FF9800', color: '#FFFFFF' }, // Orange for In Progress
                      processing: { bg: '#FF9800', color: '#FFFFFF' }, // Orange (same as in_progress)
                      sent_via_jiffy: { bg: '#0F1638', color: '#FFFFFF' }, // Dark Blue for Submitted
                      collected_via_jiffy: { bg: '#9C27B0', color: '#FFFFFF' }, // Purple
                      sent_via_other: { bg: '#00BCD4', color: '#FFFFFF' }, // Cyan
                      submitted: { bg: '#0F1638', color: '#FFFFFF' }, // Dark Blue (same as sent_via_jiffy)
                      completed: { bg: '#4CAF50', color: '#FFFFFF' }, // Green for Completed
                      attested: { bg: '#2E7D32', color: '#FFFFFF' }, // Darker Green for Attested
                    };
                    return statusMap[status] || { bg: '#6E759A', color: '#FFFFFF' }; // Default gray
                  };
                  
                  const getStatusLabel = (status) => {
                    if (!status) return 'Pending';
                    const labelMap = {
                      assigned: 'Pending',
                      pending: 'Pending',
                      in_progress: 'In Progress',
                      processing: 'In Progress',
                      sent_via_jiffy: 'Submitted',
                      submitted: 'Submitted',
                      completed: 'Completed',
                      attested: 'Attested',
                    };
                    return labelMap[status] || status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                  };
                  
                  const statusStyle = getStatusBadgeStyle(assignment.current_status || 'pending');
                  
                  return (
                    <TableRow key={assignment.id} className={classes.tableRow}>
                      <TableCell 
                        className={classes.tableCell}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Document name clicked, assignment:', assignment);
                          const doc = documents.find((d) => d.id === assignment.record_id || d.id === String(assignment.record_id));
                          console.log('Found document:', doc);
                          if (doc) {
                            handleViewHistory(doc);
                          } else {
                            // If document not found in array, create a minimal document object with the record_id
                            const docToView = {
                              id: assignment.record_id,
                              record_id: assignment.record_id,
                              document_name: getDocumentName(assignment.record_id),
                            };
                            console.log('Document not found, using minimal object:', docToView);
                            handleViewHistory(docToView);
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#0F1638',
                            fontFamily: 'Montserrat',
                            fontWeight: 500,
                            userSelect: 'none',
                            '&:hover': {
                              color: '#0A0F25',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {getDocumentName(assignment.record_id)}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          {doc?.case_number || assignment.case_number || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          {assignment.created_at
                            ? new Date(assignment.created_at).toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric',
                              })
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={getStatusLabel(assignment.current_status || 'pending')}
                          size="small"
                          style={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 600,
                            fontSize: '12px',
                            fontFamily: 'Montserrat',
                            height: '24px',
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography
                          style={{
                            fontSize: '14px',
                            color: '#131C4C',
                            fontFamily: 'Montserrat',
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={assignment.notes || ''}
                        >
                          {assignment.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                        <Button
                          size="small"
                          startIcon={<QrCodeIcon style={{ fontSize: '16px' }} />}
                          onClick={() => handleShowQrCode(assignment)}
                          style={{
                            textTransform: 'none',
                            color: '#0F1638',
                            fontSize: '12px',
                            fontFamily: 'Montserrat',
                            padding: '4px 8px',
                            minWidth: 'auto',
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(15, 22, 56, 0.08)',
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                      <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap', width: '280px', minWidth: '280px', maxWidth: '280px' }}>
                        <Box style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'nowrap' }}>
                          <Button
                            size="small"
                            onClick={() => setSendPackageDialogOpen({ 
                              document: doc || { id: assignment.record_id, document_name: getDocumentName(assignment.record_id) },
                              assignment: assignment
                            })}
                            variant="outlined"
                            style={{
                              textTransform: 'none',
                              fontSize: '12px',
                              fontFamily: 'Montserrat',
                              padding: '6px 10px',
                              minWidth: 'auto',
                              whiteSpace: 'nowrap',
                              borderColor: '#E0E0E0',
                              color: '#131C4C',
                              backgroundColor: '#FFFFFF',
                            }}
                            sx={{
                              '&:hover': {
                                borderColor: '#BDBDBD',
                                backgroundColor: '#F9F9F9',
                              },
                            }}
                          >
                            Send Package
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setAssignmentForStatusUpdate(assignment);
                              setStatusUpdateDialogOpen(true);
                              setStatusUpdateStep(0);
                              setStatusUpdateData({
                                pending: { notes: '' },
                                processing: { notes: '' },
                                archived: { notes: '' },
                              });
                            }}
                            variant="contained"
                            disabled={assignment.current_status === 'archived'}
                            style={{
                              textTransform: 'none',
                              fontSize: '12px',
                              fontFamily: 'Montserrat',
                              padding: '6px 12px',
                              minWidth: 'auto',
                              whiteSpace: 'nowrap',
                              backgroundColor: assignment.current_status === 'archived' ? '#BCC0D5' : '#0F1638',
                              color: '#FFFFFF',
                              fontWeight: 500,
                            }}
                            sx={{
                              '&:hover': {
                                backgroundColor: assignment.current_status === 'archived' ? '#BCC0D5' : '#0A0F25',
                              },
                              '&.Mui-disabled': {
                                backgroundColor: '#BCC0D5',
                                color: '#FFFFFF',
                              },
                            }}
                          >
                            Update Status
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
        );
      })()}

      {/* Assignment Tracking Section - Hide for user privilege */}
      {(() => {
        const privilege = localStorage.getItem('previlage') || sessionStorage.getItem('previlage');
        if (privilege === 'dms_user') return null;
        
        return (
          <Box>
        <Box style={{ marginBottom: '24px' }}>
          <Typography
            variant="h5"
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#131C4C',
              marginBottom: '4px',
              fontFamily: 'Montserrat',
              lineHeight: '1.2',
            }}
          >
            Assignment Tracking
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: '14px',
              color: '#6E759A',
              fontFamily: 'Montserrat',
              lineHeight: '1.4',
            }}
          >
            Monitor progress of assigned documents
          </Typography>
        </Box>

        <TableContainer 
          component={Paper}
          style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Table className={classes.table} sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Document</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Employee</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Assigned Date</TableCell>
                <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell className={classes.tableHeader}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: 'center', padding: '48px' }}>
                    <Typography style={{ color: '#6E759A', fontSize: '14px', fontFamily: 'Montserrat' }}>
                      No assignments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((assignment) => (
                  <TableRow key={assignment.id} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                          maxWidth: '250px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={getDocumentName(assignment.record_id)}
                      >
                        {getDocumentName(assignment.record_id)}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {getEmployeeName(assignment.assigned_to)}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      {assignment.created_at
                        ? new Date(assignment.created_at).toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                          })
                        : '-'}
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      {assignment.current_status ? (
                        <FormControl size="small" style={{ minWidth: 180 }}>
                          <Select
                            value={assignment.current_status}
                            onChange={(e) => handleStatusChange(assignment.id, e.target.value)}
                            disabled={updatingStatus[assignment.id]}
                            sx={{
                              fontFamily: 'Montserrat',
                              fontSize: '12px',
                              fontWeight: 600,
                              height: '28px',
                              backgroundColor: getStatusColor(assignment.current_status),
                              color: 'white',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'transparent',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                              },
                              '& .MuiSelect-icon': {
                                color: 'white',
                              },
                            }}
                          >
                            <MenuItem value="assigned" style={{ fontFamily: 'Montserrat', fontSize: '12px' }}>
                              Assigned
                            </MenuItem>
                            <MenuItem value="sent_via_jiffy" style={{ fontFamily: 'Montserrat', fontSize: '12px' }}>
                              Sent Via Jiffy
                            </MenuItem>
                            <MenuItem value="collected_via_jiffy" style={{ fontFamily: 'Montserrat', fontSize: '12px' }}>
                              Collected Via Jiffy
                            </MenuItem>
                            <MenuItem value="sent_via_other" style={{ fontFamily: 'Montserrat', fontSize: '12px' }}>
                              Sent Via Other
                            </MenuItem>
                            <MenuItem value="processing" style={{ fontFamily: 'Montserrat', fontSize: '12px' }}>
                              Processing
                            </MenuItem>
                            <MenuItem value="completed" style={{ fontFamily: 'Montserrat', fontSize: '12px' }}>
                              Completed
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography style={{ fontSize: '12px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={assignment.notes || ''}
                      >
                        {assignment.notes || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
        );
      })()}

      {/* Document Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedDocument(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Montserrat',
          },
        }}
      >
        <DialogContent style={{ padding: '24px' }}>
          {selectedDocument && (
            <DocumentDetail
              document={selectedDocument}
              onAssignmentSuccess={handleAssignmentSuccess}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Document Dialog */}
      {assignDialogOpen && (
        <AssignDocumentDialog
          open={true}
          onClose={() => setAssignDialogOpen(null)}
          recordId={assignDialogOpen.id}
          document={assignDialogOpen}
          existingAssignments={assignments}
          onSuccess={() => {
            fetchData();
            setAssignDialogOpen(null);
          }}
        />
      )}

      {/* AI Summary Dialog */}
      <Dialog
        open={Boolean(aiSummaryOpen)}
        onClose={() => setAiSummaryOpen(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Montserrat',
          },
        }}
      >
        <DialogContent style={{ padding: '24px' }}>
          {aiSummaryOpen && (
            <Box>
              <Typography
                variant="h6"
                style={{
                  marginBottom: '16px',
                  fontWeight: 600,
                  color: '#131C4C',
                  fontFamily: 'Montserrat',
                  fontSize: '20px',
                }}
              >
                AI Summary
              </Typography>
              <Typography
                style={{
                  color: '#131C4C',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  fontFamily: 'Montserrat',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {aiSummaryOpen.summary}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Package Dialog */}
      {sendPackageDialogOpen && (
        <SendPackageDialog
          open={true}
          onClose={() => setSendPackageDialogOpen(null)}
          document={sendPackageDialogOpen.document}
          assignment={sendPackageDialogOpen.assignment}
          onSuccess={async (data) => {
            // Status is already updated in SendPackageDialog using record_id
            // Just refresh the data to show updated status
            fetchData();
            setSendPackageDialogOpen(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmOpen)}
        onClose={() => setDeleteConfirmOpen(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Montserrat',
          },
        }}
      >
        <DialogTitle 
          style={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#131C4C',
            fontFamily: 'Montserrat',
            padding: '24px 24px 16px 24px',
          }}
        >
          Delete Document
        </DialogTitle>
        <DialogContent style={{ padding: '0 24px 24px 24px' }}>
          <Typography
            style={{
              fontSize: '14px',
              color: '#6E759A',
              fontFamily: 'Montserrat',
              lineHeight: '1.5',
            }}
          >
            Are you sure you want to delete "{deleteConfirmOpen?.document_name || 'this document'}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px 24px 24px', gap: '12px' }}>
          <Button
            onClick={() => setDeleteConfirmOpen(null)}
            className={classes.buttonSecondary}
            disabled={deleting}
            style={{
              textTransform: 'none',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 24px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            className={classes.button}
            disabled={deleting}
            style={{
              textTransform: 'none',
              backgroundColor: '#DC3545',
              color: '#FFFFFF',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 24px',
              '&:hover': {
                backgroundColor: '#C82333',
              },
            }}
          >
            {deleting ? (
              <CircularProgress size={20} style={{ color: '#FFFFFF' }} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <EditDocumentDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setDocumentToEdit(null);
        }}
        document={documentToEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Document History Dialog */}
      <DocumentHistory
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setDocumentForHistory(null);
        }}
        document={documentForHistory}
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Montserrat',
          },
        }}
      >
        <DialogTitle
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '24px 24px 16px 24px',
            borderBottom: 'none',
          }}
        >
          <Box style={{ flex: 1 }}>
            <Typography
              style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#131C4C',
                fontFamily: 'Montserrat',
                marginBottom: '4px',
              }}
            >
              Document QR Code
            </Typography>
            {assignmentForQr && (
              <Typography
                style={{
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#6E759A',
                  fontFamily: 'Montserrat',
                }}
              >
                {getDocumentName(assignmentForQr.record_id)}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => setQrDialogOpen(false)}
            size="small"
            style={{
              color: '#6E759A',
              padding: '4px',
              marginLeft: '16px',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {assignmentForQr && (
            <>
              <Box
                style={{
                  padding: '32px',
                  backgroundColor: '#F5F5F5',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: '320px',
                  minHeight: '320px',
                }}
              >
                <QRCode
                  value={generateTrackingUrl(assignmentForQr)}
                  size={256}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                />
              </Box>
              <Typography
                style={{
                  color: '#131C4C',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  marginBottom: '32px',
                  maxWidth: '400px',
                }}
              >
                Scan this QR code to access document tracking form
              </Typography>
              
              {/* Document Information */}
              <Box
                style={{
                  width: '100%',
                  marginBottom: '32px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {(() => {
                  const doc = documents.find((d) => d.id === assignmentForQr.record_id);
                  const assignment = assignments.find((a) => a.id === assignmentForQr.id || a.record_id === assignmentForQr.record_id);
                  const statusColor = getStatusColor(assignment?.current_status || assignmentForQr.current_status || 'assigned');
                  const statusLabel = getStatusLabel(assignment?.current_status || assignmentForQr.current_status || 'assigned');
                  
                  return (
                    <>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography style={{ fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                          Document ID:
                        </Typography>
                        <Typography style={{ fontSize: '14px', color: '#131C4C', fontFamily: 'Montserrat', fontWeight: 600 }}>
                          {assignmentForQr.record_id}
                        </Typography>
                      </Box>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography style={{ fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                          Type:
                        </Typography>
                        <Typography style={{ fontSize: '14px', color: '#131C4C', fontFamily: 'Montserrat', fontWeight: 600 }}>
                          {doc?.document_type || doc?.type || 'Certificate'}
                        </Typography>
                      </Box>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography style={{ fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                          Case Number:
                        </Typography>
                        <Typography style={{ fontSize: '14px', color: '#131C4C', fontFamily: 'Montserrat', fontWeight: 600 }}>
                          {assignmentForQr?.case_number || doc?.case_number || doc?.case_id || `CASE-2025-${String(assignmentForQr.record_id).padStart(3, '0')}`}
                        </Typography>
                      </Box>
                      {(assignment?.current_status || assignmentForQr.current_status) && (
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography style={{ fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
                            Status:
                          </Typography>
                          <Chip
                            label={statusLabel}
                            size="small"
                            style={{
                              backgroundColor: statusColor,
                              color: '#FFFFFF',
                              fontSize: '12px',
                              fontFamily: 'Montserrat',
                              fontWeight: 600,
                              height: '24px',
                              borderRadius: '12px',
                            }}
                          />
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          style={{
            padding: '24px',
            borderTop: '1px solid #E5E7EB',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {assignmentForQr && (
            <>
              <Typography
                style={{
                  fontSize: '14px',
                  color: '#131C4C',
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Share QR via:
              </Typography>
              <Box
                style={{
                  display: 'flex',
                  gap: '12px',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <Button
                  onClick={() => {
                    const url = generateTrackingUrl(assignmentForQr);
                    const subject = encodeURIComponent(`Document QR Code - ${getDocumentName(assignmentForQr.record_id)}`);
                    const body = encodeURIComponent(`Please scan the attached QR code to access the document tracking form.\n\nTracking URL: ${url}`);
                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  }}
                  className={classes.button}
                  variant="contained"
                  startIcon={<EmailIcon style={{ fontSize: '18px' }} />}
                  style={{
                    textTransform: 'none',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '12px 24px',
                    flex: 1,
                    maxWidth: '200px',
                    backgroundColor: '#0F1638',
                    color: '#FFFFFF',
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#0A0F25',
                    },
                  }}
                >
                  Email
                </Button>
                <Button
                  onClick={() => {
                    const url = generateTrackingUrl(assignmentForQr);
                    const message = encodeURIComponent(`Document QR Code - ${getDocumentName(assignmentForQr.record_id)}\n\nPlease scan the QR code to access the document tracking form.\n\nTracking URL: ${url}`);
                    window.open(`https://wa.me/?text=${message}`, '_blank');
                  }}
                  className={classes.button}
                  variant="contained"
                  startIcon={<WhatsAppIcon style={{ fontSize: '18px' }} />}
                  style={{
                    textTransform: 'none',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '12px 24px',
                    flex: 1,
                    maxWidth: '200px',
                    backgroundColor: '#0F1638',
                    color: '#FFFFFF',
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#0A0F25',
                    },
                  }}
                >
                  WhatsApp
                </Button>
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog with 3 Steps */}
      <Dialog
        open={statusUpdateDialogOpen}
        onClose={() => {
          setStatusUpdateDialogOpen(false);
          setAssignmentForStatusUpdate(null);
          setStatusUpdateStep(0);
          setStatusUpdateData({
            pending: { notes: '' },
            processing: { notes: '' },
            archived: { notes: '' },
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            fontFamily: 'Montserrat',
          },
        }}
      >
        <DialogTitle
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 16px 24px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Typography
            style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#131C4C',
              fontFamily: 'Montserrat',
            }}
          >
            Update Document Status
          </Typography>
          <IconButton
            onClick={() => {
              setStatusUpdateDialogOpen(false);
              setAssignmentForStatusUpdate(null);
              setStatusUpdateStep(0);
              setStatusUpdateData({
                pending: { notes: '' },
                processing: { notes: '' },
                completed: { notes: '' },
              });
            }}
            size="small"
            style={{ color: '#6E759A' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: '24px' }}>
          <Stepper activeStep={statusUpdateStep} style={{ marginBottom: '32px' }}>
            <Step>
              <StepLabel style={{ fontFamily: 'Montserrat' }}>Pending</StepLabel>
            </Step>
            <Step>
              <StepLabel style={{ fontFamily: 'Montserrat' }}>Processing</StepLabel>
            </Step>
            <Step>
              <StepLabel style={{ fontFamily: 'Montserrat' }}>Archived</StepLabel>
            </Step>
          </Stepper>

          {statusUpdateStep === 0 && (
            <Box>
              <Typography
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#131C4C',
                  fontFamily: 'Montserrat',
                  marginBottom: '8px',
                }}
              >
                Step 1: Pending Status
              </Typography>
              <Typography
                style={{
                  fontSize: '14px',
                  color: '#6E759A',
                  fontFamily: 'Montserrat',
                  marginBottom: '16px',
                }}
              >
                Add notes for the pending status update
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={statusUpdateData.pending.notes}
                onChange={(e) =>
                  setStatusUpdateData({
                    ...statusUpdateData,
                    pending: { notes: e.target.value },
                  })
                }
                placeholder="Enter notes for pending status..."
                style={{ fontFamily: 'Montserrat' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>
          )}

          {statusUpdateStep === 1 && (
            <Box>
              <Typography
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#131C4C',
                  fontFamily: 'Montserrat',
                  marginBottom: '8px',
                }}
              >
                Step 2: Processing Status
              </Typography>
              <Typography
                style={{
                  fontSize: '14px',
                  color: '#6E759A',
                  fontFamily: 'Montserrat',
                  marginBottom: '16px',
                }}
              >
                Add notes for the processing status update
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={statusUpdateData.processing.notes}
                onChange={(e) =>
                  setStatusUpdateData({
                    ...statusUpdateData,
                    processing: { notes: e.target.value },
                  })
                }
                placeholder="Enter notes for processing status..."
                style={{ fontFamily: 'Montserrat' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>
          )}

          {statusUpdateStep === 2 && (
            <Box>
              <Typography
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#131C4C',
                  fontFamily: 'Montserrat',
                  marginBottom: '8px',
                }}
              >
                Step 3: Archived Status
              </Typography>
              <Typography
                style={{
                  fontSize: '14px',
                  color: '#6E759A',
                  fontFamily: 'Montserrat',
                  marginBottom: '16px',
                }}
              >
                Add notes for the archived status update
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={statusUpdateData.archived.notes}
                onChange={(e) =>
                  setStatusUpdateData({
                    ...statusUpdateData,
                    archived: { notes: e.target.value },
                  })
                }
                placeholder="Enter notes for archived status..."
                style={{ fontFamily: 'Montserrat' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          style={{
            padding: '16px 24px 24px 24px',
            borderTop: '1px solid #E5E7EB',
            gap: '12px',
          }}
        >
          <Button
            onClick={() => {
              if (statusUpdateStep > 0) {
                setStatusUpdateStep(statusUpdateStep - 1);
              } else {
                setStatusUpdateDialogOpen(false);
                setAssignmentForStatusUpdate(null);
                setStatusUpdateStep(0);
                setStatusUpdateData({
                  pending: { notes: '' },
                  processing: { notes: '' },
                  completed: { notes: '' },
                });
              }
            }}
            className={classes.buttonSecondary}
            style={{
              textTransform: 'none',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 24px',
            }}
          >
            {statusUpdateStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            onClick={async () => {
              if (!assignmentForStatusUpdate) return;
              
              try {
                setUpdatingStatus({ ...updatingStatus, [assignmentForStatusUpdate.id]: true });
                
                let status = '';
                let notes = '';
                
                // Determine status and notes based on current step
                if (statusUpdateStep === 0) {
                  status = 'assigned';
                  notes = statusUpdateData.pending.notes;
                } else if (statusUpdateStep === 1) {
                  status = 'processing';
                  notes = statusUpdateData.processing.notes;
                } else if (statusUpdateStep === 2) {
                  status = 'archived';
                  notes = statusUpdateData.archived.notes;
                }
                
                // Update status via API
                await updateAssignmentStatus(
                  assignmentForStatusUpdate.id,
                  status,
                  notes,
                  null
                );
                
                // Refresh data
                const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
                const [assignmentsData, summaryData] = await Promise.all([
                  getAssignments(),
                  getDashboardSummary(userId ? { user_id: userId } : {}),
                ]);
                setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
                setSummary(summaryData);
                
                // Move to next step or close if on last step
                if (statusUpdateStep < 2) {
                  setStatusUpdateStep(statusUpdateStep + 1);
                } else {
                  // Close dialog after final update
                  setStatusUpdateDialogOpen(false);
                  setAssignmentForStatusUpdate(null);
                  setStatusUpdateStep(0);
                  setStatusUpdateData({
                    pending: { notes: '' },
                    processing: { notes: '' },
                    completed: { notes: '' },
                  });
                }
              } catch (err) {
                console.error('Failed to update status:', err);
                setError(err.detail || err.error || 'Failed to update status');
              } finally {
                setUpdatingStatus({ ...updatingStatus, [assignmentForStatusUpdate.id]: false });
              }
            }}
            className={classes.button}
            variant="contained"
            disabled={updatingStatus[assignmentForStatusUpdate?.id]}
            style={{
              textTransform: 'none',
              backgroundColor: '#0F1638',
              color: '#FFFFFF',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 500,
              padding: '8px 24px',
            }}
          >
            {updatingStatus[assignmentForStatusUpdate?.id] ? (
              <CircularProgress size={20} style={{ color: '#FFFFFF' }} />
            ) : (
              'Update'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withStyles(DocumentMSStyle)(DashboardSummary);

